// gioco/stanza.ts — la partita vista dal telefono: entra nella stanza, manda le mosse, riceve lo stato.
// Tutto ciò che conta lo decide il server; qui si tiene solo l'ultima "vista" ricevuta e il token per rientrare.
import { useSyncExternalStore } from "react";
import { tempoReale } from "../servizi/tempoReale.ts";
import { idDispositivo } from "../servizi/dispositivo.ts";
import { dimenticaToken, salvaToken } from "../servizi/sfide.ts";
import { eSpettatore, type AzioneProva, type CodiceErroreWs, type DatiSegnale, type MessaggioClient, type MessaggioServer, type Spettatore } from "../../../condiviso/protocollo.ts";
import type { Ruolo, TipoSerie, VistaPartita } from "../../../condiviso/partita.ts";

export interface StatoStanza {
  passo: "fuori" | "entrando" | "dentro" | "errore";
  codice: string | null;
  ruolo: Ruolo | null;
  token: string | null;
  iniziatore: boolean;
  vista: VistaPartita | null;
  errore: CodiceErroreWs | null;
  /** Partita di prova contro il bot (v008). */
  prova: boolean;
  /** «Prova da solo» (v015): contro il bot, ma senza i pulsanti del tester. */
  daSolo: boolean;
  /** Torneo: chi sta guardando la partita (finale). */
  spettatori: Spettatore[];
}

const FUORI: StatoStanza = { passo: "fuori", codice: null, ruolo: null, token: null, iniziatore: false, vista: null, errore: null, prova: false, daSolo: false, spettatori: [] };
/** Errori che fanno uscire dalla stanza (gli altri sono di passaggio). */
const ERRORI_DEFINITIVI: readonly CodiceErroreWs[] = ["sfida_non_trovata", "stanza_piena", "bloccato", "sostituito", "nome_vietato"];

class Stanza {
  stato: StatoStanza = FUORI;
  private nome = "";
  private ascoltatori = new Set<() => void>();
  private ascoltatoriSegnale = new Set<(d: DatiSegnale) => void>();
  private ascoltatoriSpettatori = new Set<(da: Spettatore, d: DatiSegnale) => void>();
  private stacca: (() => void)[] = [];

  iscrivi = (f: () => void): (() => void) => {
    this.ascoltatori.add(f);
    return () => this.ascoltatori.delete(f);
  };

  private imposta(n: Partial<StatoStanza>): void {
    this.stato = { ...this.stato, ...n };
    this.ascoltatori.forEach((f) => f());
  }

  entra(codice: string, nome: string, token: string | null): void {
    this.esciLocale();
    this.nome = nome;
    this.imposta({ ...FUORI, passo: "entrando", codice, token });
    this.stacca.push(tempoReale.ascolta((m) => this.ricevi(m)));
    // A ogni (ri)collegamento si rientra con il token: la partita continua da dove era.
    this.stacca.push(
      tempoReale.alCollegamento(() => {
        if (this.stato.codice && this.stato.passo !== "errore") {
          tempoReale.invia({ tipo: "entra", codice: this.stato.codice, nome: this.nome, token: this.stato.token, dispositivo: idDispositivo() });
        }
      }),
    );
  }

  /** Uscita volontaria: il server annulla la partita in corso; poi si torna fuori. */
  esci(): void {
    if (this.stato.passo === "dentro") tempoReale.invia({ tipo: "esci" });
    if (this.stato.codice) dimenticaToken(this.stato.codice);
    this.esciLocale();
  }

  private esciLocale(): void {
    this.stacca.forEach((f) => f());
    this.stacca = [];
    this.stato = FUORI;
    this.ascoltatori.forEach((f) => f());
  }

  suSegnale(f: (d: DatiSegnale) => void): () => void {
    this.ascoltatoriSegnale.add(f);
    return () => this.ascoltatoriSegnale.delete(f);
  }

  /** Torneo: segnali WebRTC da chi guarda (ognuno ha la sua connessione video, separata da quella con l'avversario). */
  suSegnaleSpettatore(f: (da: Spettatore, d: DatiSegnale) => void): () => void {
    this.ascoltatoriSpettatori.add(f);
    return () => this.ascoltatoriSpettatori.delete(f);
  }

  segnaleSpettatore(con: Spettatore, dati: DatiSegnale): void {
    this.manda({ tipo: "segnale", dati, con });
  }

  private manda(m: MessaggioClient): void {
    if (this.stato.passo === "dentro") tempoReale.invia(m);
  }

  segnale(dati: DatiSegnale): void {
    this.manda({ tipo: "segnale", dati });
  }
  pronto(valore = true): void {
    this.manda({ tipo: "pronto", valore });
  }
  risata(soggetto: "io" | "avversario", t: number): void {
    this.manda({ tipo: "risata", soggetto, t: Math.round(t) });
  }
  haRiso(): void {
    this.manda({ tipo: "ha_riso" });
  }
  rispondi(conferma: boolean): void {
    this.manda({ tipo: "rispondi", conferma });
  }
  volto(presente: boolean): void {
    this.manda({ tipo: "volto", presente });
  }
  proponi(serie: TipoSerie): void {
    this.manda({ tipo: "proponi", serie });
  }
  accetta(): void {
    this.manda({ tipo: "accetta" });
  }
  rifiuta(): void {
    this.manda({ tipo: "rifiuta" });
  }
  /** Consenso alla clip della risata (v008). */
  consensoClip(consenso: boolean): void {
    this.manda({ tipo: "clip", consenso });
  }
  /** v038b: audio spento/acceso (lo vedono tutti; chi lo spegne non sente e non viene sentito). */
  muto(valore: boolean): void {
    this.manda({ tipo: "muto", valore });
  }
  /** v045: sì al «Video per noi» (il video della risata lasciato a Laughdown, solo se dite sì tutti e due). */
  consensoNoi(consenso: boolean): void {
    this.manda({ tipo: "noi", consenso });
  }
  /** v040: sì alle foto della partita in cui mi si vede. */
  consensoFoto(consenso: boolean): void {
    this.manda({ tipo: "foto", consenso });
  }
  /** Partita di prova: i pulsanti del tester. */
  prova(azione: AzioneProva): void {
    this.manda({ tipo: "prova", azione });
  }

  private ricevi(m: MessaggioServer): void {
    switch (m.tipo) {
      case "entrato":
        if (m.codice !== this.stato.codice) return;
        salvaToken(m.codice, m.token);
        this.imposta({ passo: "dentro", ruolo: m.ruolo, token: m.token, iniziatore: m.iniziatore, errore: null, prova: m.prova === true, daSolo: m.daSolo === true });
        return;
      case "partita":
        if (this.stato.passo !== "dentro") return;
        this.imposta({ vista: m.vista });
        return;
      case "segnale": {
        const da = m.da;
        if (da && eSpettatore(da)) this.ascoltatoriSpettatori.forEach((f) => f(da, m.dati));
        else if (!da || da === "a" || da === "b") this.ascoltatoriSegnale.forEach((f) => f(m.dati));
        return;
      }
      case "spettatori":
        if (this.stato.passo === "dentro") this.imposta({ spettatori: m.posti });
        return;
      case "errore":
        if (ERRORI_DEFINITIVI.includes(m.codice)) {
          if (m.codice !== "sostituito" && this.stato.codice) dimenticaToken(this.stato.codice);
          this.stacca.forEach((f) => f());
          this.stacca = [];
          this.imposta({ passo: "errore", errore: m.codice });
        }
        return;
      default:
        return;
    }
  }
}

export const stanza = new Stanza();

export function useStanza(): StatoStanza {
  return useSyncExternalStore(stanza.iscrivi, () => stanza.stato);
}
